import React from 'react';
import { Thermometer, Wind, Droplets, CloudRain } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface StationWeather {
  name: string;
  atoll: string;
  lat: number;
  lon: number;
  temperature_2m: number | null;
  wind_speed_10m: number | null;
  precipitation: number | null;
  relative_humidity_2m: number | null;
  weather_code: number | null;
  weather_label: string;
}

interface WeatherData {
  stations: StationWeather[];
  fetched_at: string;
}

function fmt(value: number | null, unit: string): string {
  return value !== null && value !== undefined ? `${value}${unit}` : '—';
}

export function WeatherStations() {
  const { data, loading, error } = useApi<WeatherData>('/api/weather/stations');

  if (loading) {
    return (
      <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
        Loading weather data…
      </p>
    );
  }

  if (error || !data) {
    return (
      <p className="t-body-sm" style={{ color: 'var(--color-text-muted)' }}>
        Weather data unavailable.
      </p>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'var(--space-3)' }}>
        <span className="eyebrow">Maldives Meteorology · Live Conditions</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
          {new Date(data.fetched_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Indian/Maldives' })} MVT
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 'var(--space-3)',
      }}>
        {data.stations.map((s) => (
          <div key={s.name} className="card card-accent" data-accent="info" style={{ padding: 'var(--space-4)' }}>
            <div style={{ marginBottom: 'var(--space-2)' }}>
              <span className="chip" data-tone="neutral" style={{ fontSize: '0.7rem' }}>{s.atoll}</span>
            </div>

            <div className="card-title" style={{ fontSize: '0.95rem', marginBottom: 'var(--space-1)' }}>
              {s.name}
            </div>

            <div style={{
              fontFamily: 'var(--font-mono)',
              fontSize: '1.6rem',
              fontWeight: 700,
              color: 'var(--color-text-primary)',
              marginBottom: 'var(--space-2)',
              lineHeight: 1,
            }}>
              {s.temperature_2m !== null ? `${s.temperature_2m}°C` : '—'}
            </div>

            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 'var(--space-3)' }}>
              {s.weather_label}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <Wind size={12} strokeWidth={1.75} />
                {fmt(s.wind_speed_10m, ' km/h')}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <Droplets size={12} strokeWidth={1.75} />
                {fmt(s.relative_humidity_2m, '%')}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                <CloudRain size={12} strokeWidth={1.75} />
                {fmt(s.precipitation, ' mm')}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
